// src/pages/parametrizacao/RegrasAprovacaoPanel.tsx
//
// Grade 2D (tipo × origem) para configurar a flag autoAprova de cada
// combinação. Substitui o antigo toggle global (deletado em SC 2.1).
//
// Backend (feat/aprovacao-granular):
//   GET /v1/regras-aprovacao-requisicao            — lista todas as regras
//   PUT /v1/regras-aprovacao-requisicao/{t}/{o}    — upsert por par (tipo, origem)
//
// Permissão (`configuration.listar` para ver, `configuration.editar` para
// alterar). ROLE_MASTER bypassa via hasPermission.
//
// UX:
//   - Toggle direto (sem confirm modal); optimistic update + rollback em erro.
//   - Tooltip no checkbox com `dataAtualizacao` (atualizadoPorUserId omitido
//     por decisão do dev).
//   - Sem `configuration.editar`: toggles desabilitados (somente-leitura).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiGet, apiPut } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { showAlert } from '@/contexts/DialogContext';
import { Toast } from '@/contexts/ToastContext';
import {
  TIPOS_REQUISICAO,
  ORIGENS_REQUISICAO,
  TipoRequisicao,
  OrigemRequisicao,
} from '@/constants';
import { RegraAprovacaoRequisicao, RegraAprovacaoUpsert } from '@/types/aprovacao/Aprovacao';
import { formatDateTime } from '@/utils/formatUtils';
import './RegrasAprovacaoPanel.css';

interface RegrasAprovacaoPanelProps {
  theme: 'dark' | 'light';
}

const LABELS_TIPO: Record<TipoRequisicao, string> = {
  COMPRA: 'Compra',
  PERDIDA: 'Perdida',
};

const LABELS_ORIGEM: Record<OrigemRequisicao, string> = {
  PLANEJAMENTO: 'Planejamento',
  BALCAO: 'Balcão (Contato)',
};

const cellKey = (t: TipoRequisicao, o: OrigemRequisicao) => `${t}|${o}`;

const RegrasAprovacaoPanel: React.FC<RegrasAprovacaoPanelProps> = ({ theme }) => {
  const { hasPermission } = useAuth();
  const podeEditar = hasPermission('configuration.editar');

  const [regras, setRegras] = useState<RegraAprovacaoRequisicao[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setIsLoading(true);
      try {
        const res = await apiGet('/v1/regras-aprovacao-requisicao');
        if (cancelado) return;
        setRegras(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (cancelado) return;
        if (axios.isAxiosError(err) && err.response?.data?.error) {
          await showAlert(err.response.data.error, { variant: 'error' });
        } else {
          await showAlert('Erro ao carregar regras de aprovação.', { variant: 'error' });
        }
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, []);

  const getRegra = useCallback(
    (tipo: TipoRequisicao, origem: OrigemRequisicao): RegraAprovacaoRequisicao | undefined =>
      regras.find((r) => r.tipoRequisicao === tipo && r.origem === origem),
    [regras]
  );

  const handleToggle = useCallback(
    async (tipo: TipoRequisicao, origem: OrigemRequisicao, novoValor: boolean) => {
      if (!podeEditar) return;
      const key = cellKey(tipo, origem);
      if (savingKey !== null) return; // anti-double-click while another cell is saving
      setSavingKey(key);

      // Snapshot p/ rollback
      const snapshot = regras;

      // Optimistic update
      const exists = snapshot.some((r) => r.tipoRequisicao === tipo && r.origem === origem);
      if (exists) {
        setRegras((prev) =>
          prev.map((r) =>
            r.tipoRequisicao === tipo && r.origem === origem ? { ...r, autoAprova: novoValor } : r
          )
        );
      } else {
        // Sem rule pré-existente (upsert vai criar). Mostra o estado novo na grade.
        setRegras((prev) => [
          ...prev,
          {
            id: -1,
            tipoRequisicao: tipo,
            origem,
            autoAprova: novoValor,
            dataAtualizacao: new Date().toISOString(),
            atualizadoPorUserId: null,
          },
        ]);
      }

      try {
        const payload: RegraAprovacaoUpsert = { autoAprova: novoValor };
        const res = await apiPut(`/v1/regras-aprovacao-requisicao/${tipo}/${origem}`, payload);
        const regraSalva = res.data as RegraAprovacaoRequisicao;
        setRegras((prev) => {
          const idx = prev.findIndex((r) => r.tipoRequisicao === tipo && r.origem === origem);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = regraSalva;
            return copy;
          }
          return [...prev, regraSalva];
        });
        Toast.success('Regra atualizada.');
      } catch (err) {
        setRegras(snapshot);
        if (axios.isAxiosError(err) && err.response?.data?.error) {
          await showAlert(err.response.data.error, { variant: 'error' });
        } else {
          await showAlert('Erro ao salvar regra.', { variant: 'error' });
        }
      } finally {
        setSavingKey(null);
      }
    },
    [podeEditar, savingKey, regras]
  );

  return (
    <div className={`regras-aprovacao-panel ${theme}`}>
      {isLoading && regras.length === 0 ? (
        <p>Carregando regras...</p>
      ) : (
        <table className="regras-grid">
          <thead>
            <tr>
              <th className="header-empty"></th>
              {ORIGENS_REQUISICAO.map((origem) => (
                <th key={origem}>Origem: {LABELS_ORIGEM[origem]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIPOS_REQUISICAO.map((tipo) => (
              <tr key={tipo}>
                <th className="row-header">Tipo: {LABELS_TIPO[tipo]}</th>
                {ORIGENS_REQUISICAO.map((origem) => {
                  const regra = getRegra(tipo, origem);
                  const key = cellKey(tipo, origem);
                  const isSaving = savingKey === key;
                  const tooltip = regra && regra.id >= 0
                    ? `Última atualização: ${formatDateTime(regra.dataAtualizacao)}`
                    : 'Sem registro persistido ainda';
                  return (
                    <td key={origem} className="cell" title={tooltip}>
                      <label className={`toggle-label ${isSaving ? 'saving' : ''}`}>
                        {/* Tipo PERDIDA: backend ja travou persistencia (service ignora o PUT
                            silenciosamente — aceita o request mas nao altera nada). Desabilita
                            o checkbox aqui para nao confundir o usuario e mantem o estado que
                            o backend retornar (esperado: sempre autoAprova=true ate o fluxo
                            de aprovacao para Perdida ser desenvolvido — remover este gate la). */}
                        <input
                          type="checkbox"
                          checked={regra?.autoAprova ?? false}
                          disabled={!podeEditar || isSaving || tipo === 'PERDIDA'}
                          onChange={(e) => handleToggle(tipo, origem, e.target.checked)}
                        />
                        <span>auto-aprova</span>
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RegrasAprovacaoPanel;
